import {X, AlertCircle, Loader2} from 'lucide-react'
import {InlineRow} from '@/components/shared/InlineRow'
import {useI18n} from '@/i18n'
import {Stack} from '@/components/shared/Stack'
import {Icon} from '@/components/shared/Icon'
import {Input} from '@/components/ui/input'
import {Button} from '@/components/ui/button'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import type {IdentifierFormRow} from '../types'
import {useDuplicateIdentifierCheck} from '../hooks/useDuplicateCheck'
import {useAppConfig, labelOf} from '@/hooks/use-app-config'

interface IdentifierRowProps {
    row: IdentifierFormRow
    usedTypes: Set<string>
    onChange: (updated: IdentifierFormRow) => void
    onRemove: () => void
    onPrimaryChange: () => void
    onDuplicateStatusChange: (hasError: boolean) => void
}

/* eslint-disable i18next/no-literal-string -- typed identifier type constants */
const IdentifierType = {
    EMAIL: 'EMAIL',
    UPN: 'UPN',
    EMPLOYEE_NO: 'EMPLOYEE_NO',
    CUSTOM: 'CUSTOM',
} as const
/* eslint-enable i18next/no-literal-string */

const IDENTIFIER_TYPES = [IdentifierType.EMAIL, IdentifierType.UPN, IdentifierType.EMPLOYEE_NO, IdentifierType.CUSTOM] as const

type IdentifierPlaceholders = Record<string, string>

function useIdentifierPlaceholders(): IdentifierPlaceholders {
    const i18n = useI18n()
    return {
        [IdentifierType.EMAIL]: i18n.onboarding.identity.placeholderEMAIL,
        [IdentifierType.UPN]: i18n.onboarding.identity.placeholderUPN,
        [IdentifierType.EMPLOYEE_NO]: i18n.onboarding.identity.placeholderEMPLOYEE_NO,
        [IdentifierType.CUSTOM]: i18n.onboarding.identity.placeholderCUSTOM,
    }
}

const EMAIL_RE = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+$/
const SAFE_VALUE_RE = /^[a-zA-Z0-9@._-]+$/
const MAX_IDENTIFIER_LENGTH = 500

/**
 * A single row in the identifiers section.
 * Type dropdown, value input, primary radio, remove button.
 */
export function IdentifierRow({row, usedTypes, onChange, onRemove, onPrimaryChange, onDuplicateStatusChange}: IdentifierRowProps) {
    const i18n = useI18n()
    const {identifierTypes} = useAppConfig()
    const dupCheck = useDuplicateIdentifierCheck()
    const placeholders = useIdentifierPlaceholders()

    const handleBlur = () => {
        if (row.identifierValue.trim()) {
            dupCheck.check(row.identifierType, row.identifierValue)
        }
    }

    // Notify parent of duplicate status
    if (dupCheck.exists) {
        onDuplicateStatusChange(true)
    }

    // Immediate format validation
    const value = row.identifierValue.trim()
    const isEmail = row.identifierType === IdentifierType.EMAIL
    const hasFormatError = value.length > 0 && (
        isEmail ? !EMAIL_RE.test(value) : !SAFE_VALUE_RE.test(value)
    )
    const isTooLong = row.identifierValue.length > MAX_IDENTIFIER_LENGTH

    return (
        <Stack gap="xs">
            <InlineRow>
                {/* Type */}
                {row.fixed ? (
                    <div className="w-32 shrink-0">
                        <Input
                            value={labelOf(identifierTypes, row.identifierType) || row.identifierType}
                            disabled
                            className="bg-muted"
                        />
                    </div>
                ) : (
                    <div className="w-32 shrink-0">
                        <Select
                            value={row.identifierType}
                            onValueChange={val => onChange({...row, identifierType: val})}
                        >
                            <SelectTrigger data-testid={`identifier-type-${row.identifierType.toLowerCase()}`}>
                                <SelectValue/>
                            </SelectTrigger>
                            <SelectContent>
                                {IDENTIFIER_TYPES
                                    .filter(t => t === row.identifierType || !usedTypes.has(t))
                                    .map(t => (
                                        <SelectItem key={t} value={t}>
                                            {labelOf(identifierTypes, t) || t}
                                        </SelectItem>
                                    ))}
                            </SelectContent>
                        </Select>
                    </div>
                )}

                {/* Value */}
                <div className="flex-1">
                    <Input
                        value={row.identifierValue}
                        onChange={e => {
                            onChange({...row, identifierValue: e.target.value})
                            if (dupCheck.exists) {
                                dupCheck.reset()
                                onDuplicateStatusChange(false)
                            }
                        }}
                        onBlur={handleBlur}
                        placeholder={placeholders[row.identifierType] ?? i18n.onboarding.identity.identifierValuePlaceholder}
                        maxLength={MAX_IDENTIFIER_LENGTH}
                        data-testid={`identifier-value-${row.identifierType.toLowerCase()}`}
                    />
                </div>

                {/* Primary radio */}
                <label className="items-center gap-1 shrink-0 cursor-pointer">
                    <input
                        type="radio"
                        name="primary-identifier"
                        checked={row.primary}
                        onChange={onPrimaryChange}
                        className="accent-primary"
                    />
                    <span className="text-xs text-muted-foreground">{i18n.onboarding.identity.primary}</span>
                </label>

                {/* Remove button (not for fixed/email row) */}
                {!row.fixed && (
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={onRemove}
                        className="shrink-0"
                        data-testid={`identifier-remove-${row.identifierType.toLowerCase()}`}
                    >
                        <Icon icon={X}/>
                    </Button>
                )}
                {row.fixed && <div className="w-9 shrink-0"/>}
            </InlineRow>

            {/* Format validation error */}
            {hasFormatError && (
                <div className="items-center gap-1.5 text-destructive text-xs pl-[8.5rem]">
                    <AlertCircle className="h-3 w-3"/>
                    {isEmail ? i18n.onboarding.validation.invalidEmail : i18n.onboarding.validation.unsafeChars}
                </div>
            )}

            {/* Length error */}
            {isTooLong && (
                <div className="items-center gap-1.5 text-destructive text-xs pl-[8.5rem]">
                    <AlertCircle className="h-3 w-3"/>
                    {i18n.onboarding.validation.identifierMaxLength}
                </div>
            )}

            {/* Duplicate error */}
            {dupCheck.exists && !hasFormatError && (
                <div className="items-center gap-1.5 text-destructive text-xs pl-[8.5rem]">
                    <AlertCircle className="h-3 w-3"/>
                    {i18n.onboarding.identity.duplicateError({type: labelOf(identifierTypes, row.identifierType) || row.identifierType})}
                </div>
            )}

            {/* Checking indicator */}
            {dupCheck.checking && !hasFormatError && (
                <div className="items-center gap-1.5 text-muted-foreground text-xs pl-[8.5rem]">
                    <Loader2 className="h-3 w-3 animate-spin"/>
                    {i18n.common.loading}
                </div>
            )}
        </Stack>
    )
}
